import { DAY, FlexColumn, FlexRow } from '@gorila-shared-ui/components';
import { DatePicker, DatepickerOverrides } from 'baseui/datepicker';
import { ArrowRight } from 'baseui/icon';
import { TimePicker } from 'baseui/timepicker';
import { LabelSmall } from 'baseui/typography';
import { isAfter, isBefore } from 'date-fns';
import { useRef } from 'react';
import { useStyles } from '../../hooks/useStyles';
import LocalizedDatePicker from '../shared/LocalizedDatePicker';

type Props = {
  dates: (Date | null | undefined)[];
  onDatesChange: (dates: (Date | null | undefined)[]) => void;
  startLabel?: string;
  endLabel?: string;
  startDisabled?: boolean;
  endDisabled?: boolean;
  onError?: (error: string) => void;
  todayIsMaxDate?: boolean;
  minStartDate?: number;
  minEndDate?: number;
  maxEndDate?: number;
  maxDayInterval?: number;
  hideTimes?: boolean;
  inline?: boolean;
  inset?: boolean;
  inputsWidth?: number;
  beforeTodayErrorText?: string;
  beforeErrorText?: string;
  beforeMinEndDateErrorText?: string;
  beforeMinStartDateErrorText?: string;
};

export function StyledDateTimeRangePicker({
  dates,
  onDatesChange,
  startLabel = 'Start Datetime',
  endLabel = 'End Datetime',
  startDisabled = false,
  endDisabled = false,
  onError = () => {},
  todayIsMaxDate = true,
  minStartDate,
  minEndDate,
  maxEndDate,
  maxDayInterval,
  hideTimes = false,
  inline = false,
  inset = false,
  inputsWidth = 120,
  beforeTodayErrorText = 'La fecha de llegada no puede ser menor a la fecha actual',
  beforeErrorText = 'La fecha de llegada no puede ser menor a la fecha de salida',
  beforeMinEndDateErrorText = 'La llegada debe ser mayor al inicio más la duración estimada',
  beforeMinStartDateErrorText = 'La fecha de salida no puede ser menor a la fecha actual',
}: Props) {
  const { css, theme, classes } = useStyles();
  const inputWidth = css({
    width: (inline && inset ? (inputsWidth >= 150 ? inputsWidth : 150) : inputsWidth) + 'px',
  });
  const startDate = dates[0];
  const endDate = dates[1];

  const today = new Date();
  const todayTime = today.getTime();
  const localMinEndDate = minEndDate
    ? new Date(minEndDate)
    : todayIsMaxDate
      ? (startDate?.getTime() || 0) < todayTime
        ? today
        : startDate
      : startDate;

  const startRef = useRef<DatePicker<Date>>(null);
  const endRef = useRef<DatePicker<Date>>(null);

  const getMaxEndDate = () => {
    if (maxDayInterval) {
      const time = startDate ? startDate.getTime() + maxDayInterval * DAY : new Date().getTime() + maxDayInterval * DAY;
      if (maxEndDate && isAfter(time, maxEndDate)) {
        return new Date(maxEndDate);
      }
      return new Date(time);
    } else if (maxEndDate) return new Date(maxEndDate);
    else return undefined;
  };

  const datePickerOverrides = (startEnhancer: React.ReactNode): DatepickerOverrides => ({
    Input: {
      props: {
        overrides: {
          StartEnhancer: {
            style: {
              paddingLeft: theme.sizing.scale400,
              paddingRight: 0,
              fontWeight: '600',
              whiteSpace: 'nowrap',
            },
          },
          Root: {
            style: {
              paddingLeft: 0,
            },
          },
          Input: {
            style: {
              paddingLeft: theme.sizing.scale200,
              paddingRight: theme.sizing.scale500,
            },
          },
        },
        startEnhancer,
      },
    },
  });

  return (
    <FlexRow
      classNames={classes.centeredStart}
      gap={inline ? theme.sizing.scale300 : undefined}
    >
      <PickerWrapper inline={inline}>
        {!inset && <LabelSmall whiteSpace="nowrap">{startLabel}</LabelSmall>}
        <FlexRow gap={theme.sizing.scale200}>
          <div className={inputWidth}>
            <LocalizedDatePicker
              size="compact"
              value={startDate}
              onClose={() => {
                onDatesChange([startDate, endDate]);
              }}
              onChange={({ date }) => {
                if (endDate && isAfter(date as Date, endDate)) {
                  onDatesChange([date as Date, date as Date]);
                } else if (date && maxDayInterval) {
                  let maxDate = (date as Date).getTime() + maxDayInterval * DAY;
                  if (maxEndDate && isAfter(maxDate, maxEndDate)) {
                    onDatesChange([date as Date, new Date(maxEndDate)]);
                  } else if (endDate && isAfter(endDate, maxDate)) {
                    onDatesChange([date as Date, new Date(maxDate)]);
                  } else {
                    onDatesChange([date as Date, endDate]);
                  }
                } else {
                  onDatesChange([date as Date | null | undefined, endDate]);
                }
              }}
              mask="9999/99/99"
              disabled={startDisabled}
              minDate={minStartDate ? new Date(minStartDate) : undefined}
              maxDate={maxEndDate ? new Date(maxEndDate) : undefined}
              ref={startRef}
              overrides={
                inline && inset
                  ? datePickerOverrides(
                      <span
                        onClick={() => {
                          startRef.current?.open();
                        }}
                      >
                        {startLabel}
                      </span>,
                    )
                  : undefined
              }
            />
          </div>
          {!hideTimes && (
            <div className={inputWidth}>
              <TimePicker
                size="compact"
                creatable
                value={startDate}
                disabled={startDisabled}
                onChange={(time) => {
                  if (time && endDate) {
                    const beforeMinStartDate = minStartDate ? isBefore(time, minStartDate) : false;
                    if (minStartDate && beforeMinStartDate) {
                      onError(beforeMinStartDateErrorText);
                    } else if (isAfter(time, endDate)) {
                      onDatesChange([time, time]);
                    } else {
                      onDatesChange([time, endDate]);
                    }
                  }
                }}
                minTime={minStartDate ? new Date(minStartDate) : undefined}
                maxTime={maxEndDate ? new Date(maxEndDate) : undefined}
              />
            </div>
          )}
        </FlexRow>
      </PickerWrapper>
      <ArrowRight size={24} />
      <PickerWrapper inline={inline}>
        {!inset && <LabelSmall whiteSpace="nowrap">{endLabel}</LabelSmall>}
        <FlexRow gap={theme.sizing.scale200}>
          <div className={inputWidth}>
            <LocalizedDatePicker
              size="compact"
              value={endDate}
              onClose={() => {
                onDatesChange([startDate, endDate]);
              }}
              onChange={({ date }) => {
                if (date && startDate) {
                  const before = isBefore(date as Date, startDate);
                  const beforeToday = isBefore(date as Date, today);
                  const beforeMinEndDate = minEndDate ? isBefore(date as Date, minEndDate) : false;
                  if (todayIsMaxDate && beforeToday) {
                    onError(beforeTodayErrorText);
                  } else if (beforeMinEndDate) {
                    onError(beforeMinEndDateErrorText);
                  } else if (before && !startDisabled) {
                    onDatesChange([date as Date, date as Date]);
                    onError('');
                  } else if (before && startDisabled) {
                    onError(beforeErrorText);
                  } else {
                    onDatesChange([startDate, date as Date | null | undefined]);
                    onError('');
                  }
                } else {
                  onDatesChange([startDate, date as Date | null | undefined]);
                  onError('');
                }
              }}
              mask="9999/99/99"
              disabled={endDisabled}
              minDate={localMinEndDate}
              maxDate={getMaxEndDate()}
              ref={endRef}
              overrides={
                inline && inset
                  ? datePickerOverrides(
                      <span
                        onClick={() => {
                          endRef.current?.open();
                        }}
                      >
                        {endLabel}
                      </span>,
                    )
                  : undefined
              }
            />
          </div>
          {!hideTimes && (
            <div className={inputWidth}>
              <TimePicker
                size="compact"
                creatable
                value={endDate}
                disabled={endDisabled}
                onChange={(time) => {
                  if (time && startDate) {
                    const before = isBefore(time, startDate);
                    const beforeToday = isBefore(time, today);
                    const beforeMinEndDate = minEndDate ? isBefore(time, minEndDate) : false;
                    if (todayIsMaxDate && beforeToday) {
                      onError(beforeTodayErrorText);
                    } else if (beforeMinEndDate) {
                      onError(beforeMinEndDateErrorText);
                    } else if (before && !startDisabled) {
                      onDatesChange([time as Date, time as Date]);
                      onError('');
                    } else if (before && startDisabled) {
                      onError(beforeErrorText);
                    } else {
                      onDatesChange([startDate, time as Date]);
                      onError('');
                    }
                  }
                }}
                maxTime={
                  isBefore(new Date((endDate?.getTime() ?? 0) + 1 * DAY), getMaxEndDate() ?? 0)
                    ? undefined
                    : getMaxEndDate()
                }
              />
            </div>
          )}
        </FlexRow>
      </PickerWrapper>
    </FlexRow>
  );
}

const PickerWrapper = ({ inline, children }: { inline: boolean; children: React.ReactNode }) => {
  const { theme, classes } = useStyles();

  if (inline) {
    return (
      <FlexRow
        gap={theme.sizing.scale200}
        classNames={classes.centeredStart}
      >
        {children}
      </FlexRow>
    );
  }

  return <FlexColumn gap={theme.sizing.scale200}>{children}</FlexColumn>;
};
